import { useCallback, useEffect, useMemo, useState } from 'react'
import { UserRound, Building2, CalendarDays, Mail, Phone, Pencil, Check, X, AlertCircle, ShieldCheck, Link2Off } from 'lucide-react'
import { apiJson } from '../api'
import { getUser } from '../auth'

interface Employee {
  id: string
  employeeCode: string
  firstName: string
  lastName: string
  email: string
  phone?: string
  employmentStatus: string
  joinDate: string
  orgUnitId?: string
}

interface OrgUnit {
  id: string
  name: string
}

const inputCls =
  'w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-sm outline-none transition-all placeholder:text-slate-400 focus:border-brand-400 focus:ring-4 focus:ring-brand-100'

export default function Profile() {
  const user = getUser()
  const [employee, setEmployee] = useState<Employee | null>(null)
  const [orgUnits, setOrgUnits] = useState<OrgUnit[]>([])
  const [loading, setLoading] = useState(true)
  const [unlinked, setUnlinked] = useState(false)
  const [error, setError] = useState('')
  const [editingPhone, setEditingPhone] = useState(false)
  const [phoneDraft, setPhoneDraft] = useState('')
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    try {
      const emp = await apiJson<Employee>('/employees/me')
      setEmployee(emp)
      setUnlinked(false)
    } catch (err) {
      if (err instanceof Error && err.message.includes('404')) {
        setUnlinked(true)
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load profile')
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
    apiJson<OrgUnit[]>('/org-units').then(setOrgUnits).catch(() => {})
  }, [load])

  const orgName = useMemo(
    () => orgUnits.find((u) => u.id === employee?.orgUnitId)?.name ?? '—',
    [orgUnits, employee],
  )

  const savePhone = async () => {
    setSaving(true)
    setError('')
    try {
      const updated = await apiJson<Employee>('/employees/me', {
        method: 'PATCH',
        body: JSON.stringify({ phone: phoneDraft.trim() }),
        headers: { 'Content-Type': 'application/json' },
      })
      setEmployee(updated)
      setEditingPhone(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-brand-600" />
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {error && (
        <div className="flex items-center gap-2.5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle size={16} className="shrink-0" />
          {error}
        </div>
      )}

      {/* Identity card (from SSO) */}
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="h-20 bg-gradient-to-r from-brand-600 via-brand-500 to-brand-700" />
        <div className="px-6 pb-6">
          <div className="-mt-10 flex items-end gap-4">
            <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-500 to-brand-700 text-2xl font-extrabold text-white shadow-lg ring-4 ring-white">
              {user?.initials ?? '…'}
            </div>
            <div className="min-w-0 pb-1">
              <h2 className="truncate text-xl font-extrabold tracking-tight">{user?.name ?? 'Signed-in user'}</h2>
              <p className="truncate text-sm text-slate-500">@{user?.username}</p>
            </div>
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
              <ShieldCheck size={12} className="text-brand-600" />
              Keycloak SSO
            </span>
            {user?.roles.map((r) => (
              <span key={r} className="rounded-full bg-brand-50 px-3 py-1 text-xs font-semibold capitalize text-brand-700 ring-1 ring-brand-100">
                {r}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Employee record card */}
      {unlinked ? (
        <div className="flex flex-col items-center rounded-2xl border border-slate-200 bg-white py-14 text-center shadow-sm">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
            <Link2Off size={22} />
          </div>
          <p className="mt-3 text-sm font-semibold">No employee record linked</p>
          <p className="mt-1 max-w-sm text-xs text-slate-400">
            Your SSO account isn't linked to an HR record yet. Ask an admin to set the
            <span className="font-semibold"> username </span> field on your employee record.
          </p>
        </div>
      ) : employee ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold tracking-tight">Employee record</h3>
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-500">
              {employee.employeeCode}
            </span>
          </div>

          <dl className="mt-5 grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-500">
                <UserRound size={15} />
              </span>
              <div>
                <dt className="text-xs font-semibold text-slate-400">Full name</dt>
                <dd className="text-sm font-semibold">{employee.firstName} {employee.lastName}</dd>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-500">
                <Building2 size={15} />
              </span>
              <div>
                <dt className="text-xs font-semibold text-slate-400">Department</dt>
                <dd className="text-sm font-semibold">{orgName}</dd>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-500">
                <Mail size={15} />
              </span>
              <div className="min-w-0">
                <dt className="text-xs font-semibold text-slate-400">Work email</dt>
                <dd className="truncate text-sm font-semibold">{employee.email}</dd>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-500">
                <CalendarDays size={15} />
              </span>
              <div>
                <dt className="text-xs font-semibold text-slate-400">Joined · Status</dt>
                <dd className="text-sm font-semibold">
                  {(employee.joinDate ?? '').slice(0, 10)}
                  <span className="ml-2 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium capitalize text-emerald-700 ring-1 ring-emerald-100">
                    {employee.employmentStatus}
                  </span>
                </dd>
              </div>
            </div>

            {/* Phone — self-service editable */}
            <div className="flex items-center gap-3 sm:col-span-2">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-500">
                <Phone size={15} />
              </span>
              <div className="min-w-0 flex-1">
                <dt className="text-xs font-semibold text-slate-400">Phone</dt>
                {editingPhone ? (
                  <div className="mt-1 flex max-w-sm items-center gap-2">
                    <input
                      autoFocus
                      value={phoneDraft}
                      onChange={(e) => setPhoneDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') savePhone()
                        if (e.key === 'Escape') setEditingPhone(false)
                      }}
                      placeholder="+95 9 …"
                      className={inputCls}
                    />
                    <button
                      onClick={savePhone}
                      disabled={saving}
                      className="rounded-lg bg-brand-600 p-2 text-white transition-colors hover:bg-brand-700 disabled:opacity-50"
                      title="Save"
                    >
                      <Check size={15} />
                    </button>
                    <button
                      onClick={() => setEditingPhone(false)}
                      className="rounded-lg border border-slate-200 p-2 text-slate-400 transition-colors hover:bg-slate-50"
                      title="Cancel"
                    >
                      <X size={15} />
                    </button>
                  </div>
                ) : (
                  <dd className="flex items-center gap-2 text-sm font-semibold">
                    {employee.phone || <span className="text-slate-400">Not set</span>}
                    <button
                      onClick={() => {
                        setPhoneDraft(employee.phone ?? '')
                        setEditingPhone(true)
                      }}
                      className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-brand-50 hover:text-brand-600"
                      title="Edit phone"
                    >
                      <Pencil size={13} />
                    </button>
                  </dd>
                )}
              </div>
            </div>
          </dl>

          <p className="mt-6 border-t border-slate-100 pt-4 text-xs text-slate-400">
            Only your phone number is self-editable. Contact HR to change any other field.
          </p>
        </div>
      ) : null}
    </div>
  )
}
